import React from "react";
import { Button } from "@/components";
import { Edit, Trash2, Eye } from "lucide-react";
import type { Agent } from "../interfaces";

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: "bg-green-500/10 text-green-600 dark:text-green-400",
  REVIEW: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  ARCHIVED: "bg-muted text-muted-foreground",
};

interface AgentCardProps {
  agent: Agent;
  onEdit: (agent: Agent) => void;
  onDelete: (agentId: number) => void;
  onView?: (agent: Agent) => void;
}

export const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  onEdit,
  onDelete,
  onView,
}) => {
  const statusStyle = STATUS_COLORS[agent.status] || STATUS_COLORS.ARCHIVED;

  return (
    <div
      className="border border-input rounded-lg p-4 sm:p-6 bg-card hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView?.(agent)}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-semibold truncate">{agent.name}</h3>
        </div>
        <div className="flex gap-2 sm:ml-4 shrink-0 justify-end sm:justify-start" onClick={(e) => e.stopPropagation()}>
          {onView && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onView(agent)}
              title="View agent details"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(agent)}
            title="Edit agent"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(agent.id)}
            title="Delete agent"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {agent.images && agent.images.length > 0 && (
        <div className="mb-4">
          <div className="grid grid-cols-3 gap-2">
            {agent.images.slice(0, 3).map((image, index) => (
              <div
                key={index}
                className="aspect-video rounded-md overflow-hidden bg-muted"
              >
                <img
                  src={image}
                  alt={`${agent.name} image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          {agent.images.length > 3 && (
            <p className="text-xs text-muted-foreground mt-2">
              +{agent.images.length - 3} more images
            </p>
          )}
        </div>
      )}

      {agent.videos && agent.videos.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-muted-foreground">
            {agent.videos.length} video{agent.videos.length > 1 ? "s" : ""}
          </p>
        </div>
      )}

      {agent.attributes && (
        <div className="mb-4 p-3 bg-muted/50 rounded-md">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">Gender:</span>{" "}
              <span className="font-medium capitalize">{agent.attributes.gender}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Age:</span>{" "}
              <span className="font-medium">{agent.attributes.ageGroup}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Ethnicity:</span>{" "}
              <span className="font-medium">{agent.attributes.ethnicity}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Body:</span>{" "}
              <span className="font-medium capitalize">{agent.attributes.bodyType}</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-input">
        <span>
          Created: {new Date(agent.createdAt).toLocaleDateString()}
        </span>
        <span className={`px-2 py-1 rounded text-xs font-medium ${statusStyle}`}>
          {agent.status}
        </span>
        {agent.strategyIds && agent.strategyIds.length > 0 && (
          <span className="px-2 py-1 bg-primary/10 text-primary rounded">
            {agent.strategyIds.length} plan{agent.strategyIds.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </div>
  );
};
